import { Head, Link, useForm, usePage } from '@inertiajs/react'
import { ActionIcon, Anchor, Button, Group, NativeSelect, Paper, Stack, Text, TextInput, Textarea, Title } from '@mantine/core'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { type FormEvent, useMemo, useState } from 'react'
import EmptyState from '@/components/EmptyState'
import PageLayout from '@/components/PageLayout'
import { ProjectTypeBadge } from '@/components/badges'
import type { ProjectIndexPageProps, ProjectTreeNodeProps, SharedProps } from '@/types'

function TreeNode({ node, childrenMap, level }: { node: ProjectTreeNodeProps; childrenMap: Map<string | null, ProjectTreeNodeProps[]>; level: number }) {
  const children = childrenMap.get(node.id) || []
  const hasChildren = children.length > 0
  const isGlobal = node.project_type === 'global'
  const [expanded, setExpanded] = useState(isGlobal || level === 0)

  return (
    <Stack gap="xs">
      <Paper p="sm" withBorder style={{ marginLeft: level * 20 }}>
        <Group justify="space-between" wrap="nowrap">
          <Group wrap="nowrap" gap="xs">
            {hasChildren ? (
                <ActionIcon variant="subtle" onClick={() => setExpanded((value) => !value)}>
                {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </ActionIcon>
            ) : (
              <span style={{ width: 28 }} />
            )}
            <Anchor component={Link} href={`/projects/${node.id}`} fw={500}>
              {node.name}
            </Anchor>
          </Group>
          <ProjectTypeBadge type={node.project_type} />
        </Group>
      </Paper>

      {expanded && hasChildren ? (
        <Stack gap="xs">
          {children.map((child) => (
            <TreeNode key={child.id} node={child} childrenMap={childrenMap} level={level + 1} />
          ))}
        </Stack>
      ) : null}
    </Stack>
  )
}

function NewProjectForm({ projects, orgPrefix }: { projects: ProjectTreeNodeProps[]; orgPrefix: string }) {
  const { csrf_token } = usePage<ProjectIndexPageProps>().props
  const [open, setOpen] = useState(false)

  const { data, setData, post, processing, reset } = useForm({
    project: { name: '', description: '', project_type: 'project', parent_project_id: '' },
    authenticity_token: csrf_token || '',
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    post(`${orgPrefix}/projects`, {
      preserveScroll: true,
      onSuccess: () => {
        reset()
        setOpen(false)
      },
    })
  }

  if (!open) {
    return (
      <Button variant="light" onClick={() => setOpen(true)}>
        + New Project
      </Button>
    )
  }

  return (
    <Paper component="form" onSubmit={handleSubmit} p="lg">
      <Stack gap="md">
        <TextInput
          id="proj-name"
          label="Name"
          value={data.project.name}
          onChange={(e) => setData('project', { ...data.project, name: e.target.value })}
        />

        <Textarea
          id="proj-desc"
          label="Description"
          autosize
          minRows={2}
          value={data.project.description}
          onChange={(e) => setData('project', { ...data.project, description: e.target.value })}
        />

        <NativeSelect
          id="proj-type"
          label="Type"
          value={data.project.project_type}
          onChange={(e) => setData('project', { ...data.project, project_type: e.target.value })}
          data={[
            { value: 'project', label: 'Project' },
            { value: 'folder', label: 'Folder' },
          ]}
        />

        <NativeSelect
          id="proj-parent"
          label="Parent"
          value={data.project.parent_project_id}
          onChange={(e) => setData('project', { ...data.project, parent_project_id: e.target.value })}
          data={[
            { value: '', label: 'None (root level)' },
            ...projects.map((project) => ({ value: project.id, label: project.name })),
          ]}
        />

        <Group>
          <Button type="submit" loading={processing}>Create Project</Button>
          <Button type="button" variant="default" onClick={() => { reset(); setOpen(false) }}>
            Cancel
          </Button>
        </Group>
      </Stack>
    </Paper>
  )
}

export default function ProjectsIndex() {
  const { projects, flash } = usePage<ProjectIndexPageProps>().props
  const { current_organization } = usePage<SharedProps>().props
  const orgPrefix = current_organization ? `/orgs/${current_organization.slug}` : ''

  const childrenMap = useMemo(() => {
    const map = new Map<string | null, ProjectTreeNodeProps[]>()
    for (const project of projects) {
      const key = project.parent_project_id
      if (!map.has(key)) map.set(key, [])
      map.get(key)?.push(project)
    }
    return map
  }, [projects])

  const roots = childrenMap.get(null) || []

  return (
    <>
      <Head title="Projects" />

      <PageLayout
        title="Projects"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Projects' },
        ]}
        flash={flash}
      >
        <Stack gap="lg">
          <Group justify="space-between">
            <div>
              <Title order={3}>Project tree</Title>
              <Text c="dimmed" size="sm">Folders and projects now live in Mantine primitives.</Text>
            </div>
            <NewProjectForm projects={projects} orgPrefix={orgPrefix} />
          </Group>

          {roots.length > 0 ? (
            <Stack gap="sm">
              {roots.map((node) => (
                <TreeNode key={node.id} node={node} childrenMap={childrenMap} level={0} />
              ))}
            </Stack>
          ) : (
            <EmptyState>No projects yet.</EmptyState>
          )}
        </Stack>
      </PageLayout>
    </>
  )
}
